// src/app/marketing/_components/SpamHoneypots.tsx
//
// Shared dual-honeypot component for every public marketing form.
// One component, one place to update if the standard ever changes.
//
// See docs/FORM_SPAM_PROTECTION.md §2.2 for the normative standard.
//
// USAGE:
//   const [website, setWebsite] = useState('')        // honeypot #1
//   const [nickname, setNickname] = useState('')      // honeypot #2
//   ...
//   <SpamHoneypots
//     website={website} onWebsite={setWebsite}
//     nickname={nickname} onNickname={setNickname}
//   />
//   onSubmit: include { website, nickname } in POST body so the server-side
//   evaluator sees them.
//
// CALLER MUST RENAME if a real form field collides (e.g. demo-call already
// uses `website` as a legit field — there, pass the honeypot value as a
// different name in the POST body but keep the off-screen rendering rules).

'use client'

import { ChangeEvent } from 'react'

export interface SpamHoneypotsProps {
  // Honeypot #1 — conventionally named `website`. Bots trained on this
  // pattern know to leave it blank; the second honeypot catches them.
  website: string
  onWebsite: (value: string) => void
  // Honeypot #2 — conventionally named `nickname`. Bots not trained to
  // skip this fill it; this was the exact failure in the 2026-05-19 CT
  // incident.
  nickname: string
  onNickname: (value: string) => void
  // Optional rename when the form already uses `website` as a real field
  // (currently only demo-call). Defaults to "website" / "nickname".
  honeypot1Name?: string
  honeypot2Name?: string
}

// Off-screen CSS — bots that inspect `type="hidden"` won't trip but bots
// that auto-fill any visible input will. Keep all five attributes per the
// standard.
const HIDDEN_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '-9999px',
  width: '1px',
  height: '1px',
  opacity: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
}

export function SpamHoneypots({
  website,
  onWebsite,
  nickname,
  onNickname,
  honeypot1Name = 'website',
  honeypot2Name = 'nickname',
}: SpamHoneypotsProps) {
  return (
    <div aria-hidden="true" style={HIDDEN_STYLE}>
      <label>
        {/* Generic label keeps the field looking "real" to a DOM-scraping bot */}
        Website (do not fill)
        <input
          type="text"
          name={honeypot1Name}
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onWebsite(e.target.value)
          }
          defaultValue=""
        />
      </label>
      <label>
        Nickname (do not fill)
        <input
          type="text"
          name={honeypot2Name}
          tabIndex={-1}
          autoComplete="off"
          value={nickname}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onNickname(e.target.value)
          }
          defaultValue=""
        />
      </label>
    </div>
  )
}
